import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "../../components/ui/sonner";
import { StatusBadge } from "../../components/admin/StatusBadge";
import { ReasonModal } from "../../components/admin/ReasonModal";
import { ConfirmDialog } from "../../components/admin/ConfirmDialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { Button } from "../../components/ui/button";
import { formatDate, formatDateTime } from "../../lib/dateUtils";

export default function UserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [reasonModal, setReasonModal] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  const load = () => {
    api.get(`/admin/users/${id}`).then(({ data }) => setData(data));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const setUserStatus = async (newStatus, reason) => {
    try {
      await api.put(`/admin/users/${id}/status`, { status: newStatus, reason });
      toast.success("User status updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update status");
    }
  };

  if (!data) return <p className="font-medium text-midnight-black/60" data-testid="user-detail-loading">Loading...</p>;
  const { user, photographer_profile_id, sessions, bookings, reviews, messages_sent, activity_logs } = data;

  return (
    <div data-testid="user-detail-page" className="max-w-4xl">
      <button onClick={() => navigate("/admin/users")} data-testid="back-to-users" className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-midnight-black/60 hover:text-stranger-orange mb-6">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Users
      </button>

      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <h2 className="font-extrabold uppercase text-2xl">{user.first_name} {user.last_name}</h2>
          <p className="text-sm font-medium text-midnight-black/60">{user.email} · {user.role}</p>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={user.status} testId="user-detail-status" />
          {user.role !== "super_admin" && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="user-detail-actions-trigger" className="rounded-full font-bold text-xs">
                  ACTIONS
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {user.status !== "active" && <DropdownMenuItem data-testid="detail-action-activate" onClick={() => setConfirmDialog({ action: "active", label: "Activate" })}>Activate</DropdownMenuItem>}
                {user.status !== "suspended" && <DropdownMenuItem data-testid="detail-action-suspend" onClick={() => setReasonModal({ action: "suspended", label: "Suspend" })}>Suspend</DropdownMenuItem>}
                {user.status !== "banned" && <DropdownMenuItem data-testid="detail-action-ban" onClick={() => setReasonModal({ action: "banned", label: "Ban" })}>Ban</DropdownMenuItem>}
                {user.status !== "deleted" && <DropdownMenuItem data-testid="detail-action-delete" className="text-red-600" onClick={() => setConfirmDialog({ action: "deleted", label: "Delete" })}>Delete</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-10">
        <div>
          <h3 className="font-extrabold uppercase text-xs tracking-wide mb-3 text-midnight-black/50">Account Information</h3>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between border-b border-soft-gray pb-2"><dt className="font-bold">Phone</dt><dd>{user.phone || "Not set"}</dd></div>
            <div className="flex justify-between border-b border-soft-gray pb-2"><dt className="font-bold">Created</dt><dd>{formatDate(user.created_at)}</dd></div>
            <div className="flex justify-between border-b border-soft-gray pb-2"><dt className="font-bold">Profile Completed</dt><dd>{user.profile_completed ? "Yes" : "No"}</dd></div>
            {user.status_reason && <div className="flex justify-between border-b border-soft-gray pb-2"><dt className="font-bold">Status Reason</dt><dd className="text-right">{user.status_reason}</dd></div>}
          </dl>
        </div>
        <div>
          <h3 className="font-extrabold uppercase text-xs tracking-wide mb-3 text-midnight-black/50">Related Profile</h3>
          {photographer_profile_id ? (
            <Link to={`/admin/photographers/${photographer_profile_id}`} data-testid="link-to-photographer-profile" className="inline-flex items-center gap-2 text-sm font-bold text-stranger-orange hover:underline">
              View Photographer Profile <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          ) : user.role === "stranger" ? (
            <p className="text-sm text-midnight-black/60">Stranger profile data (preferences, boundaries) is stored directly on this account.</p>
          ) : (
            <p className="text-sm text-midnight-black/60">No linked profile.</p>
          )}
        </div>
      </div>

      <div className="mb-10">
        <h3 className="font-extrabold uppercase text-xs tracking-wide mb-3 text-midnight-black/50">Account Activity</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
          <div className="border border-soft-gray rounded-xl p-4"><p className="text-xl font-extrabold">{sessions.length}</p><p className="text-[10px] font-bold uppercase text-midnight-black/50">Sessions</p></div>
          <div className="border border-soft-gray rounded-xl p-4"><p className="text-xl font-extrabold">{bookings.length}</p><p className="text-[10px] font-bold uppercase text-midnight-black/50">Bookings</p></div>
          <div className="border border-soft-gray rounded-xl p-4"><p className="text-xl font-extrabold">{reviews.length}</p><p className="text-[10px] font-bold uppercase text-midnight-black/50">Reviews</p></div>
          <div className="border border-soft-gray rounded-xl p-4"><p className="text-xl font-extrabold">{messages_sent}</p><p className="text-[10px] font-bold uppercase text-midnight-black/50">Messages Sent</p></div>
        </div>
        {bookings.length > 0 && (
          <div className="space-y-2" data-testid="user-bookings-list">
            {bookings.map((b) => (
              <div key={b.id} className="text-sm font-medium border-b border-soft-gray pb-2 flex justify-between">
                <span>{b.session_title}</span>
                <StatusBadge status={b.status} />
              </div>
            ))}
          </div>
        )}
        {sessions.length > 0 && (
          <div className="space-y-2" data-testid="user-sessions-list">
            {sessions.map((s) => (
              <div key={s.id} className="text-sm font-medium border-b border-soft-gray pb-2 flex justify-between">
                <span>{s.title} · {formatDate(s.session_date)}</span>
                <StatusBadge status={s.status} />
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="font-extrabold uppercase text-xs tracking-wide mb-3 text-midnight-black/50">Audit Trail (This Account)</h3>
        {activity_logs.length === 0 ? (
          <p className="text-sm text-midnight-black/50" data-testid="no-user-activity-logs">No recorded admin activity for this account.</p>
        ) : (
          <div className="space-y-2" data-testid="user-activity-logs">
            {activity_logs.map((log) => (
              <div key={log.id} className="text-sm font-medium border-b border-soft-gray pb-2 flex justify-between">
                <span>{log.admin_name} — {log.action.replace(/_/g, " ")}</span>
                <span className="text-midnight-black/40 text-xs">{formatDateTime(log.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <ReasonModal
        open={!!reasonModal}
        onOpenChange={(v) => !v && setReasonModal(null)}
        title={`${reasonModal?.label} this account`}
        description="This action is logged. Please provide a reason."
        confirmLabel={reasonModal?.label?.toUpperCase()}
        onConfirm={(reason) => setUserStatus(reasonModal.action, reason)}
      />
      <ConfirmDialog
        open={!!confirmDialog}
        onOpenChange={(v) => !v && setConfirmDialog(null)}
        title={`${confirmDialog?.label} this account?`}
        description={confirmDialog?.action === "deleted" ? "This soft-deletes the account. Transactional history is preserved and this can be undone." : "This will restore full account access."}
        confirmLabel={confirmDialog?.label?.toUpperCase()}
        destructive={confirmDialog?.action === "deleted"}
        onConfirm={() => setUserStatus(confirmDialog.action)}
      />
    </div>
  );
}
